import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const ReflectionPrompt = ({ 
  isOpen, 
  onClose, 
  friendName, 
  contactType, 
  onSaveReflection 
}) => {
  const [reflection, setReflection] = useState({
    mood: '',
    topics: [],
    highlights: '',
    nextSteps: '',
    overall: ''
  });

  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [customTopic, setCustomTopic] = useState('');

  const getContactTypeLabel = (type) => {
    const typeMap = {
      'call': t('contactTypeCall'),
      'email': t('contactTypeEmail'),
      'text': t('contactTypeText'),
      'video': t('contactTypeVideo'),
      'meeting': t('contactTypeMeeting'),
      'other': t('contactTypeOther')
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Initialize custom topic when entering step 1
  useEffect(() => {
    if (currentStep === 1) {
      const otherLabel = t('topicOther');
      const otherTopic = reflection.topics.find(topic => topic.startsWith(`${otherLabel}: `));
      if (otherTopic) {
        setCustomTopic(otherTopic.replace(`${otherLabel}: `, ''));
      }
    }
  }, [currentStep, reflection.topics, t]);

  const moodOptions = [
    { value: 'excellent', label: t('moodExcellent'), emoji: '😊', color: '#10B981' },
    { value: 'good', label: t('moodGood'), emoji: '🙂', color: '#22C55E' },
    { value: 'okay', label: t('moodOkay'), emoji: '😐', color: '#F59E0B' },
    { value: 'difficult', label: t('moodDifficult'), emoji: '😔', color: '#EF4444' }
  ];

  const topicOptions = [
    t('topicWorkCareer'),
    t('topicFamilyRelationships'),
    t('topicHealthWellness'),
    t('topicHobbiesInterests'),
    t('topicTravelAdventures'),
    t('topicGoalsDreams'),
    t('topicChallengesStruggles'),
    t('topicCelebrationsAchievements'),
    t('topicCurrentEvents'),
    t('topicMemoriesNostalgia'),
    t('topicFuturePlans'),
    t('topicOther')
  ];

  const handleTopicToggle = (topic) => {
    const otherLabel = t('topicOther');
    if (topic === otherLabel) {
      // Check if "Other" or any "Other: ..." is already selected
      const hasOther = reflection.topics.includes(otherLabel) || reflection.topics.some(t => t.startsWith(`${otherLabel}: `));
      
      if (hasOther) {
        // Deselect: remove "Other" and any "Other: ..." entries
        setReflection(prev => ({
          ...prev,
          topics: prev.topics.filter(t => t !== otherLabel && !t.startsWith(`${otherLabel}: `))
        }));
        setCustomTopic('');
      } else {
        // Select: add "Other"
        setReflection(prev => ({
          ...prev,
          topics: [...prev.topics, otherLabel]
        }));
      }
    } else {
      // For regular topics, toggle normally
      setReflection(prev => ({
        ...prev,
        topics: prev.topics.includes(topic)
          ? prev.topics.filter(t => t !== topic)
          : [...prev.topics, topic]
      }));
    }
  };

  const handleCustomTopicChange = (value) => {
    setCustomTopic(value);
    // Update topics array: remove "Other" and any old "Other: ..." entries, then add new one if value exists
    const otherLabel = t('topicOther');
    setReflection(prev => {
      const filteredTopics = prev.topics.filter(t => t !== otherLabel && !t.startsWith(`${otherLabel}: `));
      if (value.trim()) {
        return {
          ...prev,
          topics: [...filteredTopics, `${otherLabel}: ${value.trim()}`]
        };
      } else {
        // If value is empty but "Other" was selected, keep "Other" in the list
        return {
          ...prev,
          topics: prev.topics.includes(otherLabel) ? [...filteredTopics, otherLabel] : filteredTopics
        };
      }
    });
  };

  const handleSave = () => {
    const reflectionData = {
      ...reflection,
      friendName,
      contactType,
      timestamp: new Date().toISOString(),
      step: currentStep
    };
    
    onSaveReflection(reflectionData);
    onClose();
    
    // Reset form
    setReflection({
      mood: '',
      topics: [],
      highlights: '',
      nextSteps: '',
      overall: ''
    });
    setCurrentStep(0);
    setCustomTopic('');
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 0: return t('stepTitleHowWasConversation');
      case 1: return t('stepTitleWhatDidYouTalkAbout');
      case 2: return t('stepTitleWhatWereHighlights');
      case 3: return t('stepTitleAnyNextSteps');
      case 4: return t('stepTitleOverallReflection');
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 0: return t('stepDescRateConversation');
      case 1: return t('stepDescSelectTopics');
      case 2: return t('stepDescBestMoments');
      case 3: return t('stepDescPlansCommitments');
      case 4: return t('stepDescFinalThoughts');
      default: return '';
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0: return reflection.mood !== '';
      case 1: return reflection.topics.length > 0;
      case 2: return true; // Optional step (highlights)
      case 3: return true; // Optional step (next steps)
      case 4: return true; // Optional step (overall reflection)
      default: return false;
    }
  };

  console.log('ReflectionPrompt render:', { isOpen, friendName, contactType });
  
  if (!isOpen) return null;

  return (
    <div className="reflection-overlay">
      <div className="reflection-modal">
        <div className="reflection-header">
          <div className="reflection-title">
            <h3>{t('quickReflection')}</h3>
            <p>{t('howWasYourContact', { contactType: getContactTypeLabel(contactType), name: friendName })}</p>
          </div>
          <button className="reflection-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="reflection-progress">
          <div className="progress-steps">
            {[0, 1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`progress-step ${step <= currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
              >
                <div className="step-number">{step + 1}</div>
              </div>
            ))}
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentStep + 1) / 5) * 100}%` }}
            />
          </div>
        </div>

        <div className="reflection-content">
          <div className="step-header">
            <h4>{getStepTitle()}</h4>
            <p>{getStepDescription()}</p>
          </div>

          <div className="step-content">
            {currentStep === 0 && (
              <div className="mood-selection">
                {moodOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`mood-option ${reflection.mood === option.value ? 'selected' : ''}`}
                    onClick={() => setReflection(prev => ({ ...prev, mood: option.value }))}
                    style={{ 
                      borderColor: reflection.mood === option.value ? option.color : '#E5E7EB',
                      backgroundColor: reflection.mood === option.value ? `${option.color}10` : 'white'
                    }}
                  >
                    <span className="mood-emoji">{option.emoji}</span>
                    <span className="mood-label">{option.label}</span>
                  </button>
                ))}
              </div>
            )}

            {currentStep === 1 && (
              <div className="topics-selection">
                <div className="topics-grid">
                  {topicOptions.map((topic) => (
                    <button
                      key={topic}
                      className={`topic-option ${reflection.topics.includes(topic) || reflection.topics.some(t => t.startsWith('Other: ')) ? 'selected' : ''}`}
                      onClick={() => handleTopicToggle(topic)}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
                {(reflection.topics.includes(t('topicOther')) || reflection.topics.some(topic => topic.startsWith(`${t('topicOther')}: `))) && (
                  <div className="custom-topic-input" style={{ marginTop: '16px' }}>
                    <input
                      type="text"
                      value={customTopic}
                      onChange={(e) => handleCustomTopicChange(e.target.value)}
                      placeholder={t('pleaseSpecify')}
                      className="reflection-input"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="highlights-input">
                <textarea
                  value={reflection.highlights}
                  onChange={(e) => setReflection(prev => ({ ...prev, highlights: e.target.value }))}
                  placeholder={t('placeholderHighlights')}
                  rows="4"
                  className="reflection-textarea"
                />
              </div>
            )}

            {currentStep === 3 && (
              <div className="next-steps-input">
                <textarea
                  value={reflection.nextSteps}
                  onChange={(e) => setReflection(prev => ({ ...prev, nextSteps: e.target.value }))}
                  placeholder={t('placeholderNextSteps')}
                  rows="3"
                  className="reflection-textarea"
                />
              </div>
            )}

            {currentStep === 4 && (
              <div className="overall-input">
                <textarea
                  value={reflection.overall}
                  onChange={(e) => setReflection(prev => ({ ...prev, overall: e.target.value }))}
                  placeholder={t('placeholderOverall')}
                  rows="4"
                  className="reflection-textarea"
                />
              </div>
            )}
          </div>
        </div>

        <div className="reflection-actions">
          <button 
            className="btn btn-secondary" 
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            {t('back')}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
            disabled={!isStepValid()}
          >
            {currentStep === 4 ? t('saveReflection') : t('next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReflectionPrompt;
